import { useState } from "react"
import { ScheduleTopmost } from "routes/schedule/scheduleIntroduction"
import { Alert, Box, Stack, Tooltip, Typography } from "@mui/material"
import { LoadingButton } from "@mui/lab"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { PublishedItemRight, ScheduleItemRight } from "slot-definitions"
import { ListItemBox } from "lib/@components/ListItemBox"
import { Secure } from "lib/authorization/secure"
import { User } from "lib/user/user"
import { createDraft } from "controller/create-draft-schedule"
import { navigate } from "lib/routes/navigate"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { uniqueId } from "library/database/split-id.mjs"
import { useSchedule } from "routes/schedule/lib/useSchedule"
import { RegimeDraftLockTooltip } from "routes/regime/regime-draft-locking/regime-draft-lock-tooltip"

ScheduleTopmost("**").plug(<ScheduleOutOfDate />)

function ScheduleOutOfDate() {
    const { target } = useBoundContext()
    const [busy, setBusy] = useState(false)
    return (
        target.mismatchedVersion && (
            <Box py={3}>
                <Alert
                    severity="warning"
                    data-cy="schedule-out-of-date-banner"
                    sx={{
                        "& .MuiAlert-message": {
                            width: 1,
                        },
                    }}
                >
                    <ListItemBox width={1}>
                        <Stack spacing={1} sx={{ flex: 1, width: 1 }}>
                            <Typography component="div" variant="subtitle1" gutterBottom>
                                Schedule Out Of Date
                            </Typography>
                            <Typography component="div" sx={{ maxWidth: 500 }} variant="body" gutterBottom>
                                The schedule from which this one derives has changed and so this schedule should be
                                reviewed to ensure that it follows current legislation and best practice.
                            </Typography>
                        </Stack>
                        <Box>
                            <Secure
                                hide
                                demand="scheduleEditor"
                                fallback={
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Published By
                                        </Typography>
                                        <User id={target?.metadata?.publishedBy} />
                                    </Box>
                                }
                            >
                                <RegimeDraftLockTooltip>
                                    <LoadingButton
                                        loading={busy}
                                        onClick={review}
                                        data-cy="schedule-out-of-date-review-button"
                                    >
                                        Review
                                    </LoadingButton>
                                </RegimeDraftLockTooltip>
                            </Secure>
                        </Box>
                    </ListItemBox>
                </Alert>
            </Box>
        )
    )

    async function review() {
        setBusy(true)
        try {
            await createDraft(target._id)
        } catch (error) {
            console.error(error)
            errorSnackbar("Couldn't open the review. Please try again.")
            setBusy(false)
            return
        }
        navigate(
            `/app/schedules/compare/draft!${uniqueId(target._id)}/scheduleHistory!${uniqueId(
                target.originatingScheduleId
            )}-${target.originatingScheduleVersion}/schedule!${uniqueId(target.originatingScheduleId)}`
        )
    }
}

PublishedItemRight.plug(<ListItemOutOfDate priority={-2} />)
ScheduleItemRight.plug(<ListItemOutOfDate priority={-2} />)

function ListItemOutOfDate({ schedule: item }) {
    const result = useSchedule(item._id, item.version ?? "no_v")
    const schedule = result?.schedule ?? { mismatchedVersion: undefined }
    const { mismatchedVersion: isMismatched } = schedule

    return (
        !!isMismatched && (
            <Tooltip arrow title="The original schedule, from which this derived, has changed">
                <Box pt={1} data-cy="out-of-date-schedule-icon">
                    <Iconify icon="ep:warning-filled" sx={{ color: "warning.main", fontSize: 24 }} />
                </Box>
            </Tooltip>
        )
    )
}
